export function StorageShedInfo({ level, stars, assignedCount }: { level: number; stars: number; assignedCount: number }) {
    // Constants matching the backend StorageShedService
    const LEVEL_REFUND_PERCENTAGES: Record<number, number> = { 1: 10, 2: 12, 3: 15, 4: 18, 5: 20 };
    const STAR_CHANCE_PERCENTAGES: Record<number, number> = { 1: 5, 2: 10, 3: 15, 4: 20, 5: 25 };
    const CHARACTER_BONUS_PERCENTAGE = 2;

    // Calculate current bonuses
    const baseRefundPercentage = LEVEL_REFUND_PERCENTAGES[level] || 10;
    const baseRefundChance = STAR_CHANCE_PERCENTAGES[stars] || 5;
    const characterBonus = assignedCount * CHARACTER_BONUS_PERCENTAGE;
    const finalRefundPercentage = baseRefundPercentage + characterBonus;
    const finalRefundChance = baseRefundChance + characterBonus;

    return (
        <div className="mb-6">
            <div className="flex items-center gap-3 mb-4">
                <span className="text-2xl">📦</span>
                <h2 className="text-xl font-serif text-amber-900">Material Refund Bonus</h2>
            </div>

            <p className="text-amber-800 font-serif text-sm mb-4 leading-relaxed">
                The Storage Shed provides a chance to refund a percentage of materials spent on construction and crafting.
                Higher levels increase the refund amount, while more stars increase the chance of refund occurring.
                Assigned workers boost both values.
            </p>

            {/* Current Status */}
            <div className="rounded-lg p-4 mb-4 bg-blue-900/20 border border-blue-700/50">
                <h3 className="text-sm font-semibold uppercase tracking-wider mb-3 text-blue-400">
                    Current Refund Stats
                </h3>
                <div className="grid grid-cols-2 gap-4">
                    <div className="text-center">
                        <div className="text-2xl font-bold text-blue-300">{finalRefundChance}%</div>
                        <div className="text-xs text-amber-700">Chance of Refund</div>
                    </div>
                    <div className="text-center">
                        <div className="text-2xl font-bold text-green-700">{finalRefundPercentage}%</div>
                        <div className="text-xs text-amber-700">Refund Amount</div>
                    </div>
                </div>
                {assignedCount > 0 && (
                    <div className="text-xs font-serif text-amber-700 mt-3 text-center">
                        {assignedCount} worker{assignedCount !== 1 ? 's' : ''} assigned (+{characterBonus}% to both)
                    </div>
                )}
            </div>

            {/* How It Works */}
            <div className="mb-4">
                <h3 className="text-sm font-semibold text-amber-900 mb-2">How It Works</h3>
                <div className="bg-amber-100/40 rounded-lg p-3 border border-amber-400/30 text-xs text-amber-700 space-y-2">
                    <p>
                        <strong className="text-amber-900">On completion:</strong> When a building upgrade or crafting job finishes,
                        the game rolls to see if a refund occurs.
                    </p>
                    <p>
                        <strong className="text-amber-900">Current odds:</strong> {finalRefundChance}% chance to refund {finalRefundPercentage}% of materials spent.
                    </p>
                    <p>
                        <strong className="text-amber-900">Example:</strong> If you spent 100 Wood on construction and the refund triggers,
                        you'd get back {Math.floor(100 * finalRefundPercentage / 100)} Wood.
                    </p>
                </div>
            </div>

            {/* Level Refund Table */}
            <div className="mb-4">
                <h3 className="text-sm font-semibold text-amber-900 mb-2">Level - Refund Amount</h3>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-amber-400/40">
                                <th className="text-left py-2 px-3 text-amber-700 font-medium">Level</th>
                                <th className="text-center py-2 px-3 text-amber-700 font-medium">Base Refund %</th>
                            </tr>
                        </thead>
                        <tbody>
                            {[1, 2, 3, 4, 5].map((lvl) => (
                                <tr
                                    key={lvl}
                                    className={`border-b border-amber-300/30 ${level === lvl ? 'bg-blue-900/20' : ''}`}
                                >
                                    <td className="py-2 px-3 text-amber-900 font-medium">Level {lvl}</td>
                                    <td className="py-2 px-3 text-center text-green-700 font-medium">
                                        {LEVEL_REFUND_PERCENTAGES[lvl]}%
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Star Chance Table */}
            <div className="mb-4">
                <h3 className="text-sm font-semibold text-amber-900 mb-2">Stars - Refund Chance</h3>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-amber-400/40">
                                <th className="text-left py-2 px-3 text-amber-700 font-medium">Stars</th>
                                <th className="text-center py-2 px-3 text-amber-700 font-medium">Base Chance %</th>
                            </tr>
                        </thead>
                        <tbody>
                            {[1, 2, 3, 4, 5].map((s) => (
                                <tr
                                    key={s}
                                    className={`border-b border-amber-300/30 ${stars === s ? 'bg-blue-900/20' : ''}`}
                                >
                                    <td className="py-2 px-3 text-amber-900 font-medium">{'★'.repeat(s)}{'☆'.repeat(5-s)}</td>
                                    <td className="py-2 px-3 text-center text-blue-300 font-medium">
                                        {STAR_CHANCE_PERCENTAGES[s]}%
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Worker Bonus */}
            <div className="p-3 bg-amber-100/40 rounded-lg border border-amber-400/30">
                <h4 className="text-xs font-semibold text-amber-700 uppercase mb-2">Worker Bonus</h4>
                <p className="text-xs font-serif text-amber-700 mb-2">
                    Each character assigned to the Storage Shed adds +{CHARACTER_BONUS_PERCENTAGE}% to both the refund chance and refund amount.
                </p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                        <span className="text-amber-700">Current workers:</span>
                        <span className="ml-2 text-amber-900 font-medium">{assignedCount}</span>
                    </div>
                    <div>
                        <span className="text-amber-700">Total bonus:</span>
                        <span className="ml-2 text-green-700 font-medium">+{characterBonus}%</span>
                    </div>
                </div>
            </div>
        </div>
    );
}

/**
 * Conservatory-specific component showing resource production UI.
 * Styled for parchment theme with production controls.
 */
